import { useNavigate } from 'react-router-dom';
import { useCartStore } from '../store/cartStore';
import { useToastStore } from '../store/toastStore';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { Button } from '../components/ui/Button';
import { GarmentTag } from '../components/ui/GarmentTag';
import { AlertTriangleIcon } from '../components/ui/Icons';
import { WhatsAppIcon } from '../components/ui/ContactIcons';
import { usePaymentSettings } from '../hooks/usePaymentSettings';
import { getCartPixPrice } from '../utils/pricing';
import { formatReservationDuration } from '../utils/reservation';
import { getCampaignCatalogHref } from '../config/promotionCampaign';
import { CartItem } from '../models';
import { CartEmpty } from './cart/CartEmpty';
import { CartItemRow } from './cart/CartItemRow';
import { CartMobileBar, CartSummary } from './cart/CartSummary';
import { RefreshIcon, ShieldIcon } from './cart/CartIcons';
import { FOCUS_RING } from './cart/cartStyles';
import { getItemStockIssue, useCartStockCheck } from './cart/useCartStockCheck';

const EXCHANGE_POLICY_HREF = '/sobre#trocas-e-devolucoes';

function CartTrustBadges() {
  return (
    <div className="flex flex-wrap justify-center lg:justify-start gap-2 mt-6">
      <GarmentTag className="sm:-rotate-1">
        <ShieldIcon />
        <span className="text-text-medium">Compra segura</span>
      </GarmentTag>
      <GarmentTag to={EXCHANGE_POLICY_HREF} className={`sm:rotate-1 min-h-11 ${FOCUS_RING}`}>
        <RefreshIcon />
        <span>Troca em até 7 dias</span>
      </GarmentTag>
      <GarmentTag className="sm:-rotate-1">
        <WhatsAppIcon className="w-4 h-4 text-olivine-dark shrink-0" />
        <span className="text-text-medium">Frete confirmado no WhatsApp</span>
      </GarmentTag>
    </div>
  );
}

interface CartNoticeProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

function CartNotice({ title, description, action }: CartNoticeProps) {
  return (
    <div role="alert" className="flex items-start gap-3 rounded-xl bg-champagne border border-chocolate/30 p-4 mb-4">
      <AlertTriangleIcon className="w-5 h-5 shrink-0 mt-0.5 text-chocolate-dark" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-text-dark">{title}</p>
        {description && <p className="text-sm text-text-medium mt-0.5">{description}</p>}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

function itemKey(item: CartItem) {
  return `${item.productId}-${item.variantId}`;
}

export function CartPage() {
  const { items, removeItem, updateQuantity, toggleGift, totalPrice, totalItems, clearCart, restoreItem } =
    useCartStore();
  const showToast = useToastStore((s) => s.show);
  const navigate = useNavigate();
  const { status, retry } = useCartStockCheck();
  const { confirm, ConfirmDialogElement } = useConfirm();
  const { settings } = usePaymentSettings();

  const catalogHref = getCampaignCatalogHref();

  if (items.length === 0) {
    return <CartEmpty catalogHref={catalogHref} />;
  }

  const itemCount = totalItems();
  const productsTotal = totalPrice();
  const issues = items.filter((i) => getItemStockIssue(i) !== null);
  const hasStockIssues = issues.length > 0;
  const pixPrice = settings ? getCartPixPrice(items, productsTotal, settings.pixDiscountPercentage) : null;
  const reservationMinutes = settings?.pixReservationMinutes ?? 0;
  const reservationText =
    reservationMinutes > 0
      ? `No Pix, suas peças ficam reservadas por ${formatReservationDuration(reservationMinutes)} até o pagamento.`
      : null;

  const ctaDisabled = status === 'checking' || hasStockIssues;
  let blockReason: string | null = null;
  if (hasStockIssues) {
    blockReason = 'Ajuste os itens marcados para continuar.';
  } else if (status === 'checking') {
    blockReason = 'Verificando disponibilidade...';
  } else if (status === 'failed') {
    blockReason = 'Não deu para conferir o estoque agora. Conferimos de novo no pagamento.';
  }

  const handleRemove = (item: CartItem) => {
    const index = items.findIndex((i) => itemKey(i) === itemKey(item));
    removeItem(item.productId, item.variantId);
    showToast('Peça removida da sacola.', 'info', {
      label: 'Desfazer',
      onClick: () => restoreItem(item, index),
    });
  };

  const handleClear = async () => {
    const confirmed = await confirm({
      title: 'Esvaziar sacola?',
      message: 'Todas as peças vão sair da sacola.',
      confirmLabel: 'Esvaziar sacola',
      cancelLabel: 'Manter peças',
      danger: true,
    });
    if (confirmed) clearCart();
  };

  const handleContinue = () => navigate('/checkout');

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8 pb-44 lg:pb-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-dark">Sua sacola</h1>
        <p className="text-sm text-text-medium mt-0.5">
          {itemCount} {itemCount === 1 ? 'item' : 'itens'}
        </p>
      </div>

      {status === 'checking' && (
        <p role="status" className="text-sm text-text-medium py-2 mb-2">
          Verificando disponibilidade...
        </p>
      )}

      {status === 'failed' && (
        <CartNotice
          title="Não foi possível conferir a disponibilidade agora."
          description="Você pode tentar de novo ou continuar: o estoque é conferido outra vez no pagamento."
          action={
            <Button variant="outline" size="sm" onClick={retry} className={`min-h-11 ${FOCUS_RING}`}>
              Tentar de novo
            </Button>
          }
        />
      )}

      {hasStockIssues && (
        <CartNotice
          title={issues.length === 1 ? 'Uma peça precisa de ajuste' : `${issues.length} peças precisam de ajuste`}
          description="Veja os itens marcados abaixo. Ajuste a quantidade ou remova da sacola para continuar."
        />
      )}

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-8 lg:items-start">
        <div>
          <ul className="flex flex-col gap-4">
            {items.map((item) => (
              <CartItemRow
                key={itemKey(item)}
                item={item}
                issue={getItemStockIssue(item)}
                onIncrease={() => updateQuantity(item.productId, item.variantId, item.quantity + 1)}
                onDecrease={() => updateQuantity(item.productId, item.variantId, item.quantity - 1)}
                onAdjust={() => updateQuantity(item.productId, item.variantId, item.stockQuantity)}
                onToggleGift={() => toggleGift(item.productId, item.variantId)}
                onRemove={() => handleRemove(item)}
              />
            ))}
          </ul>

          <div className="flex justify-center lg:justify-start mt-2">
            <button
              type="button"
              onClick={handleClear}
              className={`min-h-11 px-3 rounded-lg text-sm text-text-medium hover:text-chocolate hover:underline transition-colors ${FOCUS_RING}`}
            >
              Esvaziar sacola
            </button>
          </div>

          <CartTrustBadges />
        </div>

        <aside className="mt-6 lg:mt-0 lg:sticky lg:top-24">
          <CartSummary
            productsTotal={productsTotal}
            pixPrice={pixPrice}
            reservationText={reservationText}
            catalogHref={catalogHref}
            ctaDisabled={ctaDisabled}
            blockReason={blockReason}
            onContinue={handleContinue}
          />
        </aside>
      </div>

      <CartMobileBar
        productsTotal={productsTotal}
        ctaDisabled={ctaDisabled}
        blockReason={blockReason}
        onContinue={handleContinue}
      />

      {ConfirmDialogElement}
    </div>
  );
}
